// src/Pages/PatientDashboard/Notifications.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Button,
  Divider,
  Chip,
  Paper,
  Menu,
  MenuItem,
  Avatar,
  ListItemAvatar,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  MarkAsUnread as MarkAsUnreadIcon,
  MarkEmailRead as MarkEmailReadIcon,
  FilterList as FilterListIcon,
  NotificationsActive as NotificationsActiveIcon,
  CalendarToday as CalendarIcon,
  Payment as PaymentIcon,
  Message as MessageIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { notificationsMockData } from '../../data/patientNotificationsMockData';

const Notifications = () => {
  const [notifications, setNotifications] = useState(notificationsMockData);
  const [filter, setFilter] = useState('All');
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleMarkAsRead = (id) => {
    const updatedNotifications = notifications.map((notification) =>
      notification.id === id ? { ...notification, read: true } : notification
    );
    setNotifications(updatedNotifications);
    setSnackbar({
      open: true,
      message: 'Notification marked as read',
      severity: 'success',
    });
  };

  const handleMarkAsUnread = (id) => {
    const updatedNotifications = notifications.map((notification) =>
      notification.id === id ? { ...notification, read: false } : notification
    );
    setNotifications(updatedNotifications);
    setSnackbar({
      open: true,
      message: 'Notification marked as unread',
      severity: 'info',
    });
  };

  const handleDelete = (id) => {
    const updatedNotifications = notifications.filter(
      (notification) => notification.id !== id
    );
    setNotifications(updatedNotifications);
    setSnackbar({
      open: true,
      message: 'Notification deleted',
      severity: 'success',
    });
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = (type) => {
    if (type) {
      setFilter(type);
    }
    setAnchorEl(null);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'Appointment':
        return <CalendarIcon color="primary" />;
      case 'Payment':
        return <PaymentIcon color="success" />;
      case 'Message':
        return <MessageIcon color="info" />;
      default:
        return <NotificationsActiveIcon color="warning" />;
    }
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (filter === 'All') return true;
    return notification.type === filter;
  });

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <Box sx={{ padding: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 3,
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Notifications
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" sx={{ ml: 2 }}>
              <NotificationsActiveIcon color="action" />
            </Badge>
          )}
        </Typography>

        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={handleFilterClick}
            sx={{ borderRadius: '8px' }}
          >
            Filter
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => handleFilterClose()}
          >
            <MenuItem onClick={() => handleFilterClose('All')}>All</MenuItem>
            <MenuItem onClick={() => handleFilterClose('Appointment')}>Appointments</MenuItem>
            <MenuItem onClick={() => handleFilterClose('Payment')}>Payments</MenuItem>
            <MenuItem onClick={() => handleFilterClose('Message')}>Messages</MenuItem>
          </Menu>
          <Chip 
            label={`${filter} Notifications`}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: '8px' }}
          />
        </Stack>
      </Box>

      {/* Notifications List */}
      <Paper elevation={2} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        <List sx={{ p: 0 }}>
          {filteredNotifications.length > 0 ? (
            filteredNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                {index > 0 && <Divider />}
                <ListItem
                  sx={{
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    transition: 'background-color 0.3s ease',
                    '&:hover': {
                      bgcolor: 'action.selected',
                    },
                    py: 2,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'background.paper' }}>
                      {getNotificationIcon(notification.type)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: notification.read ? 'normal' : 'bold',
                          color: notification.read ? 'text.primary' : 'primary.main',
                        }}
                      >
                        {notification.title}
                      </Typography>
                    }
                    secondary={
                      <Stack spacing={1} sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {notification.message}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {notification.timestamp}
                        </Typography>
                        <Chip
                          label={notification.type}
                          size="small"
                          color={
                            notification.type === 'Appointment'
                              ? 'primary'
                              : notification.type === 'Payment'
                              ? 'success'
                              : 'info'
                          }
                          sx={{ alignSelf: 'flex-start' }}
                        />
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      {notification.read ? (
                        <IconButton
                          edge="end"
                          onClick={() => handleMarkAsUnread(notification.id)}
                          size="small"
                          color="primary"
                        >
                          <MarkAsUnreadIcon />
                        </IconButton>
                      ) : (
                        <IconButton
                          edge="end"
                          onClick={() => handleMarkAsRead(notification.id)}
                          size="small"
                          color="success"
                        >
                          <MarkEmailReadIcon />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        onClick={() => handleDelete(notification.id)}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))
          ) : (
            <ListItem sx={{ py: 4 }}>
              <ListItemText
                primary={
                  <Box sx={{ textAlign: 'center' }}>
                    <NotificationsActiveIcon
                      sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }}
                    />
                    <Typography variant="h6" color="text.secondary">
                      No notifications found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filter === 'All'
                        ? "You're all caught up!"
                        : `No ${filter.toLowerCase()} notifications available`}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          )}
        </List>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Notifications;
